import { useEffect, useRef } from 'react'

/**
 * One browser history entry per open layer, so Back closes what is on top.
 *
 * Android's Back gesture and iOS's edge swipe both arrive as `popstate`, and an
 * app that never pushed anything has nothing to pop — so the browser leaves the
 * app instead. Every dismissible thing (a sheet, a tab that is not the diary)
 * therefore owns a history entry while it is open and gives it back when it
 * closes. Back then walks down that stack one layer at a time and only exits
 * once the diary is all that is left.
 *
 * Entries carry their *depth*, not an identity. `history.go(-n)` fires a single
 * `popstate` however many steps it traverses, so counting pops is unreliable;
 * comparing the depth we landed on against the depth we expect always works,
 * which also makes the handler idempotent — a layer closed from a button has
 * already left the stack by the time its `popstate` arrives, and the handler
 * simply finds nothing to do.
 */

export interface BackLayer {
  /** Close this layer. Called when Back reaches it. */
  dismiss: () => void
  /** True to intercept Back — unsaved work that deserves a question first. */
  guard?: () => boolean
  /** Asked instead of dismissing while the guard holds. */
  onBlocked?: () => void
}

const STATE_KEY = 'gramasDepth'

const stack: BackLayer[] = []

/** History entries we have pushed and not yet had taken back. */
let owned = 0

let pendingFlush = false

function depthOf(state: unknown): number {
  if (typeof state !== 'object' || state === null) return 0
  const value = (state as Record<string, unknown>)[STATE_KEY]
  return typeof value === 'number' ? value : 0
}

function push(): void {
  owned += 1
  history.pushState({ [STATE_KEY]: owned }, '')
}

/**
 * Give back any entry no layer is using any more.
 *
 * Deferred to a microtask because React can unmount one layer and mount another
 * in the same commit — under StrictMode it unmounts and remounts *the same*
 * one — and reacting to each half separately would traverse history for a
 * change that never happened.
 */
function scheduleFlush(): void {
  if (pendingFlush) return
  pendingFlush = true
  queueMicrotask(() => {
    pendingFlush = false
    const excess = owned - stack.length
    if (excess <= 0) return
    owned -= excess
    history.go(-excess)
  })
}

export function pushBackLayer(layer: BackLayer): void {
  stack.push(layer)
  push()
}

export function dropBackLayer(layer: BackLayer): void {
  const index = stack.lastIndexOf(layer)
  // Already gone: Back popped it, and this is the unmount that followed.
  if (index === -1) return
  stack.splice(index, 1)
  scheduleFlush()
}

function onPopState(event: PopStateEvent): void {
  const landed = depthOf(event.state)
  // Clamped, never assigned: a pop can only ever hand entries back. Landing on
  // a deeper entry than we think we own means it is a stale one from a previous
  // page load, and adopting its depth would have us traverse past the entry the
  // app itself started on — which is to say, out of the app.
  owned = Math.min(owned, landed)

  while (stack.length > landed) {
    const top = stack[stack.length - 1]
    if (top === undefined) return
    if (top.guard?.() === true) {
      // The entry is already spent, so put one back before asking: whichever
      // way the question is answered, Back has to keep working afterwards.
      push()
      top.onBlocked?.()
      return
    }
    stack.pop()
    top.dismiss()
  }
}

if (typeof window !== 'undefined') {
  // A reload lands back inside our own pushed entry with the whole stack gone.
  // Re-baselining is what stops the first few Back presses from doing nothing.
  if (depthOf(history.state) > 0) history.replaceState(null, '')
  window.addEventListener('popstate', onPopState)
}

/**
 * Own a history entry for as long as `active` is true.
 *
 * The callbacks are read through a ref so that changing them — which for an
 * inline arrow function is every render — never re-registers the layer, which
 * would push and pop history entries on every keystroke.
 */
export function useBackLayer(
  active: boolean,
  dismiss: () => void,
  options: { guard?: () => boolean; onBlocked?: () => void } = {},
): void {
  const latest = useRef({ dismiss, ...options })
  latest.current = { dismiss, ...options }

  useEffect(() => {
    if (!active) return
    const layer: BackLayer = {
      dismiss: () => latest.current.dismiss(),
      guard: () => latest.current.guard?.() ?? false,
      onBlocked: () => latest.current.onBlocked?.(),
    }
    pushBackLayer(layer)
    return () => dropBackLayer(layer)
  }, [active])
}
